import { useMemo, useState } from "react";
import type { ScanResponse, LineItem, ExpenseCategory, BelegTyp } from "../types";
import { EXPENSE_CATEGORIES } from "../types";
import type { CompressedImage } from "../image";
import { apiSubmit, apiLookupUid, apiReport, ApiError } from "../api";
import { totalMismatch } from "../totals";
import { newReceiptId } from "../ulid";
import {
  Screen, ScreenHeader, Display, Lede,
  Field, Input, Btn, SectionLabel, Eyebrow,
} from "../ui/components";
import { Ico } from "../ui/Ico";
import { C, mono } from "../ui/tokens";
import { Spinner } from "../ui/Spinner";

interface Props {
  employee: string;
  email: string;
  images: CompressedImage[];
  initial: ScanResponse;
  belegTyp: BelegTyp;
  onSuccess: (total: number, currency: string, items: number, ref: string) => void;
  onWrongDomain: (email: string) => void;
  onScrap: () => void;
  onBack: () => void;
}

export function Edit({ employee, email, images, initial, belegTyp, onSuccess, onWrongDomain, onScrap, onBack }: Props) {
  const [merchant, setMerchant] = useState(initial.merchant);
  const [merchantUid, setMerchantUid] = useState(initial.merchantUid ?? "");
  const [receiptDate, setReceiptDate] = useState(initial.receiptDate ?? new Date().toISOString().slice(0, 10));
  const [currency, setCurrency] = useState(initial.currency || "CHF");
  const [items, setItems] = useState<LineItem[]>(initial.items);
  const [bezeichnung, setBezeichnung] = useState(initial.merchant ?? "");
  const [category, setCategory] = useState<ExpenseCategory>("Verpflegung auswärts");
  const [categoryOther, setCategoryOther] = useState("");
  const [receiptId] = useState(newReceiptId());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uidLookup, setUidLookup] = useState<{ status: "idle" | "loading" | "miss"; }>({ status: "idle" });
  const [reported, setReported] = useState(false);

  /** Report what the model returned, so a wrong read is diagnosable later. */
  function reportWrongResult() {
    setReported(true);
    void apiReport({
      kind: "wrong_result",
      detail: `pages=${images.length} reasons=${mismatch ? "total_mismatch" : "none"}`,
      pages: images.map((i) => ({ mimeType: i.mimeType, bytes: i.blob.size })),
      scan: {
        total: initial.total,
        itemCount: initial.items.length,
        confidence: initial.confidence,
      },
    });
  }

  async function lookupUid() {
    if (currency !== "CHF" || !merchant.trim()) return;
    setUidLookup({ status: "loading" });
    try {
      const res = await apiLookupUid(merchant.trim());
      if (res.uid) {
        setMerchantUid(res.uid);
        setUidLookup({ status: "idle" });
      } else {
        setUidLookup({ status: "miss" });
      }
    } catch {
      setUidLookup({ status: "miss" });
    }
  }

  const total = useMemo(
    () => items.reduce((s, i) => s + (Number.isFinite(i.amount) ? i.amount : 0), 0),
    [items]
  );

  // The submitted amount is the sum of the line items above, so a total the
  // model read but could not itemise (a page missing from a multi-page
  // invoice) would otherwise go through silently at the wrong figure.
  const mismatch = useMemo(() => totalMismatch(initial.total, items), [initial.total, items]);

  // The AI-lookup button searches Swiss registries only, so it is offered for
  // CHF receipts. Foreign tax IDs are read from the image during extraction.
  const canLookup = currency === "CHF" && merchant.trim().length > 0;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiSubmit({
        receiptId,
        employee,
        merchant: merchant.trim() || "Unbekannt",
        merchantUid: merchantUid.trim() || null,
        receiptDate,
        currency,
        items: items.filter((i) => i.description.trim().length > 0 || i.amount !== 0),
        bezeichnung: bezeichnung.trim() || merchant.trim() || "Unbekannt",
        category: category === "Andere" && categoryOther.trim()
          ? `Andere: ${categoryOther.trim()}` as ExpenseCategory
          : category,
        belegTyp,
        images: images.map((i) => ({ mimeType: i.mimeType, dataBase64: i.dataBase64 })),
      });
      onSuccess(res.total, currency, items.length, receiptId.slice(-6).toUpperCase());
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        onWrongDomain(email);
        return;
      }
      if (e instanceof ApiError && e.status === 429) {
        setError(`Zu viele Einreichungen. Bitte in ca. ${Math.round((e.retryAfter ?? 3600) / 60)} Minuten erneut versuchen.`);
      } else if (e instanceof ApiError && e.message === "kk_not_supported_before_q2_2026") {
        setError("Kreditkartenbelege sind erst ab Q2 2026 möglich. Datum prüfen oder zurück und Beleg-Typ ändern.");
      } else if (e instanceof ApiError && e.message === "rueckverg_not_supported_before_q2_2026") {
        setError("Rückvergütungen sind erst ab Q2 2026 möglich. Datum prüfen oder zurück und Beleg-Typ ändern.");
      } else if (e instanceof ApiError) {
        setError("Speichern fehlgeschlagen. Bitte erneut versuchen.");
      } else {
        setError("Netzwerkfehler. Bitte erneut versuchen.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (busy) return <Spinner />;

  return (
    <Screen>
      <ScreenHeader back={onBack} />
      <Display>Beleg prüfen</Display>
      <Lede>Wir haben das Folgende erkannt. Tippe an, um zu korrigieren.</Lede>

      {initial.confidence === "low" && !initial.parseFailed && (
        <div style={{
          marginTop: 16, padding: "10px 14px",
          background: "rgba(255,180,90,0.08)",
          border: "1px solid rgba(255,180,90,0.25)",
          borderRadius: 12, fontSize: 13, color: "rgba(255,200,140,0.9)",
        }}>
          Qualität niedrig. Bitte Werte prüfen.
        </div>
      )}

      {mismatch && (
        <div style={{
          marginTop: 16, padding: "10px 14px",
          background: "rgba(255,180,90,0.08)",
          border: "1px solid rgba(255,180,90,0.25)",
          borderRadius: 12, fontSize: 13, lineHeight: 1.5,
          color: "rgba(255,200,140,0.9)",
        }}>
          Auf dem Beleg steht eine Gesamtsumme von {mismatch.scannedTotal.toFixed(2)} {currency},
          die Positionen ergeben aber {mismatch.itemSum.toFixed(2)} {currency}.
          {mismatch.diff < 0
            ? " Fehlt eine Seite oder eine Position?"
            : " Bitte die Positionen prüfen."}
        </div>
      )}

      <div style={{ marginTop: 32 }}>
        <Field label="Händler">
          <Input value={merchant} onChange={setMerchant} />
        </Field>
        <Field
          label="MwSt / VAT-Nr."
          optional
          hint={uidLookup.status === "miss" ? "Konnte nicht gefunden werden. Bitte manuell eintragen." : undefined}
        >
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 52px",
            gap: 8,
            alignItems: "stretch",
          }}>
            <Input mono value={merchantUid} onChange={setMerchantUid} placeholder={currency === "CHF" ? "CHE-000.000.000" : "DE… / IT… / NL…"} />
            <button
              onClick={lookupUid}
              disabled={!canLookup || uidLookup.status === "loading"}
              aria-label="Mit AI suchen"
              title={currency === "CHF" ? "Mit AI suchen" : "Automatische Suche nur für Schweizer UID verfügbar"}
              style={{
                height: 52, borderRadius: 14,
                background: C.surface,
                border: `1px solid ${C.border}`,
                color: C.fg,
                cursor: canLookup ? "pointer" : "not-allowed",
                opacity: canLookup ? 1 : 0.4,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 0,
              }}
            >
              {uidLookup.status === "loading" ? (
                <div style={{
                  width: 18, height: 18, borderRadius: "50%",
                  background: "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.7) 80deg, transparent 120deg)",
                  WebkitMask: "radial-gradient(circle, transparent 7px, #000 8px)",
                  mask: "radial-gradient(circle, transparent 7px, #000 8px)",
                  animation: "spespin 1s linear infinite",
                }} />
              ) : (
                <Ico name="spark" size={18} color={C.fg2} />
              )}
            </button>
          </div>
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 12 }}>
          <Field label="Datum">
            <Input type="date" value={receiptDate} onChange={setReceiptDate} />
          </Field>
          <Field label="Währung">
            <Input
              mono value={currency}
              onChange={(v) => setCurrency(v.toUpperCase().slice(0, 3))}
              align="center"
            />
          </Field>
        </div>

        <Field label="Zuweisung">
          <CategorySelect value={category} onChange={setCategory} />
        </Field>

        <div
          aria-hidden={category !== "Andere"}
          style={{
            maxHeight: category === "Andere" ? 140 : 0,
            opacity: category === "Andere" ? 1 : 0,
            transform: category === "Andere" ? "translateY(0)" : "translateY(-4px)",
            overflow: "hidden",
            transition: [
              "max-height 240ms cubic-bezier(0.4, 0, 0.2, 1)",
              "opacity 200ms ease",
              "transform 240ms cubic-bezier(0.4, 0, 0.2, 1)",
            ].join(", "),
          }}
        >
          <Field label="Andere bitte beschreiben">
            <Input
              value={categoryOther}
              onChange={setCategoryOther}
              placeholder="z. B. Parkgebühr, Versand …"
            />
          </Field>
        </div>
      </div>

      {/* Positionen */}
      <div style={{ marginTop: 24 }}>
        <SectionLabel right={`${items.length} Pos.`}>Positionen</SectionLabel>
        <div style={{ fontSize: 12, color: C.fg3, padding: "0 4px 8px" }}>
          Lösche Positionen, die nicht zur Spesen-Abrechnung gehören. Total = Summe der verbliebenen.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((it, idx) => (
            <PositionRow
              key={idx}
              name={it.description}
              amount={it.amount}
              onNameChange={(v) => setItems((p) => p.map((x, i) => i === idx ? { ...x, description: v } : x))}
              onAmountChange={(v) => setItems((p) => p.map((x, i) => i === idx ? { ...x, amount: parseFloat(v) || 0 } : x))}
              onDelete={() => setItems((p) => p.filter((_, i) => i !== idx))}
            />
          ))}
          <button onClick={() => setItems((p) => [...p, { description: "", amount: 0 }])} style={dashedAdd}>
            <Ico name="plus" size={14} color={C.fg2} />
            Position hinzufügen
          </button>
        </div>
      </div>

      {/* Bezeichnung */}
      <div style={{ marginTop: 24 }}>
        <Field label="Bezeichnung / Bemerkung" hint="Erscheint in der Spalte „Bezeichnung / Bemerkung“ im Sheet.">
          <textarea
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.target.value)}
            placeholder="z. B. Geschäftsessen mit Kunde, Bürobedarf …"
            style={{
              width: "100%", minHeight: 80, padding: 14, boxSizing: "border-box",
              borderRadius: 14, background: C.surface, color: C.fg,
              border: `1px solid ${C.border}`, outline: "none",
              fontSize: 15, lineHeight: 1.45, resize: "vertical",
            }}
          />
        </Field>
      </div>

      {/* Total card */}
      <div style={{
        marginTop: 24, padding: 20, borderRadius: 20,
        background: C.surface, border: `1px solid ${C.border}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Eyebrow>Total</Eyebrow>
          <div style={{
            fontFamily: mono, fontSize: 26, fontWeight: 700,
            letterSpacing: "-0.02em",
          }}>
            <span style={{ fontSize: 12, color: C.fg3, marginRight: 6 }}>{currency}</span>
            {total.toFixed(2)}
          </div>
        </div>
        <div style={{ height: 1, background: C.border, margin: "14px 0" }} />
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontSize: 12, color: C.fg3,
        }}>
          <span>{items.length} Positionen</span>
          <span style={{ fontFamily: mono }}>{receiptDate}</span>
        </div>
      </div>

      {/* A partial read whose total matches its own items looks correct to the
          server, so this button is the only way we learn about it. */}
      <div style={{ marginTop: 12, textAlign: "center" }}>
        <button
          onClick={reportWrongResult}
          disabled={reported}
          style={{
            background: "none", border: "none", padding: 6,
            fontSize: 12, color: C.fg3,
            textDecoration: reported ? "none" : "underline",
            cursor: reported ? "default" : "pointer",
          }}
        >
          {reported ? "Danke, gemeldet." : "Erkennung war falsch?"}
        </button>
      </div>

      {error && (
        <div style={{
          marginTop: 12, padding: "10px 14px",
          background: C.dangerBg, color: C.danger,
          border: "1px solid rgba(255,90,90,0.22)", borderRadius: 12,
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 10 }}>
        <Btn
          onClick={submit}
          disabled={items.length === 0}
          icon={<Ico name="arrow" size={18} color="#000" />}
        >
          Einreichen
        </Btn>
        <button onClick={onScrap} style={{
          background: "transparent", border: "none", cursor: "pointer",
          color: C.fg3, fontSize: 13, padding: 14,
        }}>Verwerfen und neu starten</button>
      </div>

    </Screen>
  );
}

function CategorySelect({
  value, onChange,
}: { value: ExpenseCategory; onChange: (v: ExpenseCategory) => void }) {
  return (
    <div style={{
      width: "100%", height: 52, borderRadius: 14,
      background: C.surface, border: `1px solid ${C.border}`,
      display: "flex", alignItems: "center", padding: "0 12px 0 16px",
      position: "relative",
    }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as ExpenseCategory)}
        style={{
          flex: 1, minWidth: 0,
          background: "transparent", border: "none", outline: "none",
          color: C.fg, fontSize: 16,
          appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
          padding: 0,
          fontFamily: "inherit",
        }}
      >
        {EXPENSE_CATEGORIES.map((c) => (
          <option key={c} value={c} style={{ background: "#0e0e10" }}>{c}</option>
        ))}
      </select>
      <Ico name="chevDown" size={16} color={C.fg3} />
    </div>
  );
}

function PositionRow({
  name, amount, onNameChange, onAmountChange, onDelete,
}: {
  name: string;
  amount: number;
  onNameChange: (v: string) => void;
  onAmountChange: (v: string) => void;
  onDelete: () => void;
}) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "1fr 96px 44px",
      gap: 8,
      alignItems: "stretch",
    }}>
      <Input value={name} onChange={onNameChange} placeholder="Beschreibung" />
      <Input
        mono
        value={String(amount)}
        onChange={onAmountChange}
        align="right"
        inputMode="decimal"
      />
      <button onClick={onDelete} style={deleteBtn} aria-label="Position löschen">
        <Ico name="close" size={16} color={C.danger} />
      </button>
    </div>
  );
}

const dashedAdd: React.CSSProperties = {
  height: 44, borderRadius: 14,
  background: "transparent", border: `1px dashed ${C.border}`,
  color: C.fg2, fontSize: 14, fontWeight: 500,
  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
  cursor: "pointer",
};

const deleteBtn: React.CSSProperties = {
  width: 44, height: 52, borderRadius: 14,
  background: C.dangerBg, border: "1px solid rgba(255,90,90,0.18)",
  color: C.danger, cursor: "pointer", padding: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
};
